import React from "react";
import { Box } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import DarkCard from "components/common/DarkCard";
import { useExcursion } from "contexts/api";
import type {
  ExcursionParamsInput,
  ExcursionGroupParamsInput,
} from "api/excursions/Excursion.typed";
import { usePermissions } from "contexts/app/permissions";
import { Permission } from "api/zekay/permissions";
import { useConfirmationModal } from "contexts/app/confirmation";
import { GROUP_DEFAULT_COLOR } from "utils/colorPalette";
import { entriesFromTemplate, templateFromEntries } from "./display/paramsTemplateUtils";
import type { ParamsTemplateEntry } from "./display/paramsTemplateUtils";
import { TemplatePickerChips } from "./display/TemplatePickerChips";
import { ExcursionTemplateForm } from "./display/ExcursionTemplateForm";
import { GroupTemplateForm } from "./display/GroupTemplateForm";
import { useExcursionsData } from "contexts/app/excursionsData";
import { hrAccentColor } from "styles/globalStyles";

export default function ExcursionParamsPage() {
  const {
    getExcursionParamsById,
    createExcursionParams,
    updateExcursionParams,
    deleteExcursionParams,
    getExcursionGroupParamsById,
    createExcursionGroupParams,
    updateExcursionGroupParams,
    deleteExcursionGroupParams,
  } = useExcursion();
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const canEdit = hasPermission(Permission.EXCURSIONS_EDIT);
  const { excursionTemplates, groupTemplates, loading, reload } = useExcursionsData();

  const [savingExcursion, setSavingExcursion] = React.useState(false);
  const [savingGroup, setSavingGroup] = React.useState(false);

  const [selectedExcursionId, setSelectedExcursionId] = React.useState<number | "new" | null>(null);
  const [selectedGroupId, setSelectedGroupId] = React.useState<number | "new" | null>(null);

  const [excursionName, setExcursionName] = React.useState("");
  const [excursionDescription, setExcursionDescription] = React.useState("");
  const [excursionColor, setExcursionColor] = React.useState(GROUP_DEFAULT_COLOR);
  const [maxPeoplePerExcursion, setMaxPeoplePerExcursion] = React.useState("");
  const [maxGroupsPerExcursion, setMaxGroupsPerExcursion] = React.useState("");
  const [excursionTemplateEntries, setExcursionTemplateEntries] = React.useState<
    ParamsTemplateEntry[]
  >([]);
  const [excursionDefault, setExcursionDefault] = React.useState(false);
  const [excursionRequiresUserValidation, setExcursionRequiresUserValidation] =
    React.useState(false);

  const [groupName, setGroupName] = React.useState("");
  const [groupDescription, setGroupDescription] = React.useState("");
  const [groupColor, setGroupColor] = React.useState(GROUP_DEFAULT_COLOR);
  const [maxUsersPerGroup, setMaxUsersPerGroup] = React.useState("");
  const [maxCustomersPerGroup, setMaxCustomersPerGroup] = React.useState("");
  const [groupTemplateEntries, setGroupTemplateEntries] = React.useState<ParamsTemplateEntry[]>([]);
  const [groupDefault, setGroupDefault] = React.useState(false);
  const [groupRequiresUserValidation, setGroupRequiresUserValidation] = React.useState(false);

  React.useEffect(() => {
    if (selectedExcursionId === null || selectedExcursionId === "new") {
      if (selectedExcursionId === "new") {
        setExcursionName("");
        setExcursionDescription("");
        setExcursionColor(GROUP_DEFAULT_COLOR);
        setMaxPeoplePerExcursion("");
        setMaxGroupsPerExcursion("");
        setExcursionTemplateEntries([]);
        setExcursionDefault(false);
        setExcursionRequiresUserValidation(false);
      }
      return;
    }
    const excursionId = selectedExcursionId;
    getExcursionParamsById(excursionId).then((data) => {
      if (!data) return;
      setExcursionName(data.name ?? "");
      setExcursionDescription(data.description ?? "");
      setExcursionColor(data.color?.trim() ? data.color : GROUP_DEFAULT_COLOR);
      setMaxPeoplePerExcursion(String(data.max_people_per_excursion ?? ""));
      setMaxGroupsPerExcursion(String(data.max_groups_per_excursion ?? ""));
      setExcursionTemplateEntries(
        entriesFromTemplate(data.params_template as Record<string, unknown>),
      );
      setExcursionDefault(Boolean(data.should_be_used_by_default));
      setExcursionRequiresUserValidation(Boolean(data.requires_user_validation));
    });
  }, [selectedExcursionId, getExcursionParamsById]);

  React.useEffect(() => {
    if (selectedGroupId === null || selectedGroupId === "new") {
      if (selectedGroupId === "new") {
        setGroupName("");
        setGroupDescription("");
        setGroupColor(GROUP_DEFAULT_COLOR);
        setMaxUsersPerGroup("");
        setMaxCustomersPerGroup("");
        setGroupTemplateEntries([]);
        setGroupDefault(false);
        setGroupRequiresUserValidation(false);
      }
      return;
    }
    const groupId = selectedGroupId;
    getExcursionGroupParamsById(groupId).then((data) => {
      if (!data) return;
      setGroupName(data.name ?? "");
      setGroupDescription(data.description ?? "");
      setGroupColor(data.color?.trim() ? data.color : GROUP_DEFAULT_COLOR);
      setMaxUsersPerGroup(String(data.max_users_per_group ?? ""));
      setMaxCustomersPerGroup(String(data.max_customers_per_group ?? ""));
      setGroupTemplateEntries(entriesFromTemplate(data.params_template as Record<string, unknown>));
      setGroupDefault(Boolean(data.should_be_used_by_default));
      setGroupRequiresUserValidation(Boolean(data.requires_user_validation));
    });
  }, [selectedGroupId, getExcursionGroupParamsById]);

  const handleSaveExcursion = React.useCallback(async () => {
    if (!canEdit) return;
    const ok = await showConfirmation(
      "Enregistrer le modèle",
      selectedExcursionId === "new"
        ? "Créer ce modèle de sortie ?"
        : "Enregistrer les modifications ?",
    );
    if (!ok) return;
    setSavingExcursion(true);
    try {
      const payload: ExcursionParamsInput = {
        name: excursionName.trim() || undefined,
        description: excursionDescription.trim() || undefined,
        color: excursionColor.trim() || undefined,
        params_template: templateFromEntries(excursionTemplateEntries),
        requires_user_validation: excursionRequiresUserValidation,
        max_people_per_excursion: maxPeoplePerExcursion.trim()
          ? parseInt(maxPeoplePerExcursion, 10)
          : undefined,
        max_groups_per_excursion: maxGroupsPerExcursion.trim()
          ? parseInt(maxGroupsPerExcursion, 10)
          : undefined,
        should_be_used_by_default: excursionDefault,
      };
      if (selectedExcursionId === "new") {
        const created = await createExcursionParams(payload);
        if (created?.id != null) {
          setSelectedExcursionId(created.id);
          await reload();
        }
      } else if (typeof selectedExcursionId === "number") {
        await updateExcursionParams(selectedExcursionId, payload);
        await reload();
      }
    } finally {
      setSavingExcursion(false);
    }
  }, [
    canEdit,
    showConfirmation,
    selectedExcursionId,
    excursionName,
    excursionDescription,
    excursionColor,
    maxPeoplePerExcursion,
    maxGroupsPerExcursion,
    excursionTemplateEntries,
    excursionDefault,
    excursionRequiresUserValidation,
    createExcursionParams,
    updateExcursionParams,
    reload,
  ]);

  const handleDeleteExcursion = React.useCallback(async () => {
    if (!canEdit || selectedExcursionId === "new" || selectedExcursionId === null) return;
    const ok = await showConfirmation(
      "Supprimer le modèle",
      "Ce modèle de sortie sera supprimé. Continuer ?",
    );
    if (!ok) return;
    await deleteExcursionParams(selectedExcursionId);
    setSelectedExcursionId(
      excursionTemplates.length > 1 ? (excursionTemplates[0].id ?? null) : null,
    );
    await reload();
  }, [
    canEdit,
    showConfirmation,
    selectedExcursionId,
    excursionTemplates,
    deleteExcursionParams,
    reload,
  ]);

  const handleSaveGroup = React.useCallback(async () => {
    if (!canEdit) return;
    const ok = await showConfirmation(
      "Enregistrer le modèle",
      selectedGroupId === "new" ? "Créer ce modèle de groupe ?" : "Enregistrer les modifications ?",
    );
    if (!ok) return;
    setSavingGroup(true);
    try {
      const payload: ExcursionGroupParamsInput = {
        name: groupName.trim() || undefined,
        description: groupDescription.trim() || undefined,
        color: groupColor.trim() || undefined,
        params_template: templateFromEntries(groupTemplateEntries),
        requires_user_validation: groupRequiresUserValidation,
        max_users_per_group: maxUsersPerGroup.trim() ? parseInt(maxUsersPerGroup, 10) : undefined,
        max_customers_per_group: maxCustomersPerGroup.trim()
          ? parseInt(maxCustomersPerGroup, 10)
          : undefined,
        should_be_used_by_default: groupDefault,
      };
      if (selectedGroupId === "new") {
        const created = await createExcursionGroupParams(payload);
        if (created?.id != null) {
          setSelectedGroupId(created.id);
          await reload();
        }
      } else if (typeof selectedGroupId === "number") {
        await updateExcursionGroupParams(selectedGroupId, payload);
        await reload();
      }
    } finally {
      setSavingGroup(false);
    }
  }, [
    canEdit,
    showConfirmation,
    selectedGroupId,
    groupName,
    groupDescription,
    groupColor,
    maxUsersPerGroup,
    maxCustomersPerGroup,
    groupTemplateEntries,
    groupDefault,
    groupRequiresUserValidation,
    createExcursionGroupParams,
    updateExcursionGroupParams,
    reload,
  ]);

  const handleDeleteGroup = React.useCallback(async () => {
    if (!canEdit || selectedGroupId === "new" || selectedGroupId === null) return;
    const ok = await showConfirmation(
      "Supprimer le modèle",
      "Ce modèle de groupe sera supprimé. Continuer ?",
    );
    if (!ok) return;
    await deleteExcursionGroupParams(selectedGroupId);
    setSelectedGroupId(groupTemplates.length > 1 ? (groupTemplates[0].id ?? null) : null);
    await reload();
  }, [
    canEdit,
    showConfirmation,
    selectedGroupId,
    groupTemplates,
    deleteExcursionGroupParams,
    reload,
  ]);

  const addExcursionEntry = () =>
    setExcursionTemplateEntries((prev) => [...prev, { key: "", type: "string" }]);
  const updateExcursionEntry = (index: number, entry: ParamsTemplateEntry) =>
    setExcursionTemplateEntries((prev) => {
      const next = [...prev];
      next[index] = entry;
      return next;
    });
  const removeExcursionEntry = (index: number) =>
    setExcursionTemplateEntries((prev) => prev.filter((_, i) => i !== index));

  const addGroupEntry = () =>
    setGroupTemplateEntries((prev) => [...prev, { key: "", type: "string" }]);
  const updateGroupEntry = (index: number, entry: ParamsTemplateEntry) =>
    setGroupTemplateEntries((prev) => {
      const next = [...prev];
      next[index] = entry;
      return next;
    });
  const removeGroupEntry = (index: number) =>
    setGroupTemplateEntries((prev) => prev.filter((_, i) => i !== index));

  if (loading) {
    return (
      <Box sx={{ p: 2, width: "100%", maxWidth: "100vw" }}>
        <DarkTypography level="body-md">Chargement…</DarkTypography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        p: { xs: 1, sm: 2 },
        overflowY: "auto",
        height: "100dvh",
        maxHeight: { xs: "calc(100dvh - 80px)", sm: "calc(100dvh - 120px)" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DarkCard dense>
          <DarkTypography level="title-md" sx={{ color: hrAccentColor, mb: 2 }}>
            Modèles de sortie
          </DarkTypography>
          <Box sx={{ mb: 2 }}>
            <TemplatePickerChips
              items={excursionTemplates}
              selectedId={selectedExcursionId}
              onSelect={setSelectedExcursionId}
              onSelectNew={canEdit ? () => setSelectedExcursionId("new") : undefined}
              canEdit={canEdit}
              ariaLabelNew="Créer un modèle de sortie"
            />
          </Box>
          {selectedExcursionId !== null && (
            <ExcursionTemplateForm
              name={excursionName}
              description={excursionDescription}
              color={excursionColor}
              maxPeoplePerExcursion={maxPeoplePerExcursion}
              maxGroupsPerExcursion={maxGroupsPerExcursion}
              shouldBeUsedByDefault={excursionDefault}
              requiresUserValidation={excursionRequiresUserValidation}
              templateEntries={excursionTemplateEntries}
              onNameChange={setExcursionName}
              onDescriptionChange={setExcursionDescription}
              onColorChange={setExcursionColor}
              onMaxPeoplePerExcursionChange={setMaxPeoplePerExcursion}
              onMaxGroupsPerExcursionChange={setMaxGroupsPerExcursion}
              onShouldBeUsedByDefaultChange={setExcursionDefault}
              onRequiresUserValidationChange={setExcursionRequiresUserValidation}
              onTemplateEntriesAdd={addExcursionEntry}
              onTemplateEntriesUpdate={updateExcursionEntry}
              onTemplateEntriesRemove={removeExcursionEntry}
              canEdit={canEdit}
              saving={savingExcursion}
              onSave={handleSaveExcursion}
              onDelete={handleDeleteExcursion}
              showDelete={selectedExcursionId !== "new"}
            />
          )}
        </DarkCard>

        <DarkCard dense>
          <DarkTypography level="title-md" sx={{ color: hrAccentColor, mb: 2 }}>
            Modèles de groupe
          </DarkTypography>
          <Box sx={{ mb: 2 }}>
            <TemplatePickerChips
              items={groupTemplates}
              selectedId={selectedGroupId}
              onSelect={setSelectedGroupId}
              onSelectNew={canEdit ? () => setSelectedGroupId("new") : undefined}
              canEdit={canEdit}
              ariaLabelNew="Créer un modèle de groupe"
            />
          </Box>
          {selectedGroupId !== null && (
            <GroupTemplateForm
              name={groupName}
              description={groupDescription}
              color={groupColor}
              maxUsersPerGroup={maxUsersPerGroup}
              maxCustomersPerGroup={maxCustomersPerGroup}
              shouldBeUsedByDefault={groupDefault}
              requiresUserValidation={groupRequiresUserValidation}
              templateEntries={groupTemplateEntries}
              onNameChange={setGroupName}
              onDescriptionChange={setGroupDescription}
              onColorChange={setGroupColor}
              onMaxUsersPerGroupChange={setMaxUsersPerGroup}
              onMaxCustomersPerGroupChange={setMaxCustomersPerGroup}
              onShouldBeUsedByDefaultChange={setGroupDefault}
              onRequiresUserValidationChange={setGroupRequiresUserValidation}
              onTemplateEntriesAdd={addGroupEntry}
              onTemplateEntriesUpdate={updateGroupEntry}
              onTemplateEntriesRemove={removeGroupEntry}
              canEdit={canEdit}
              saving={savingGroup}
              onSave={handleSaveGroup}
              onDelete={handleDeleteGroup}
              showDelete={selectedGroupId !== "new"}
            />
          )}
        </DarkCard>
      </Box>
    </Box>
  );
}
